/**
 * Split a full name string into name parts.
 *
 * @param fullName - The full name string to split
 * @param options - Splitting options
 * @param options.separator - Separator to split on (default: ' ')
 * @param options.lastNamePosition - Where to position the last name:
 *   - 'last': First part is firstName, last part is lastName, middle parts are middleNames
 *   - 'first': First part is firstName, all remaining parts are lastName
 *   - 'none': Everything is firstName, no lastName
 *   (default: 'last')
 * @param options.normalize - Whether to trim and normalize whitespace (default: true)
 *
 * @returns Object containing firstName, lastName, and optionally middleNames
 *
 * @example
 * splitName('John Doe') // { firstName: 'John', lastName: 'Doe', middleNames: [] }
 * splitName('John') // { firstName: 'John', lastName: null, middleNames: [] }
 * splitName('John Michael Doe') // { firstName: 'John', lastName: 'Doe', middleNames: ['Michael'] }
 * splitName('John Michael Doe', { lastNamePosition: 'first' }) // { firstName: 'John', lastName: 'Michael Doe', middleNames: [] }
 * splitName('John Michael Doe', { lastNamePosition: 'none' }) // { firstName: 'John Michael Doe', lastName: null, middleNames: [] }
 */
export function splitName(
  fullName: string,
  options: {
    separator?: string
    lastNamePosition?: 'last' | 'first' | 'none'
    normalize?: boolean
  } = {},
): {
  firstName: string
  lastName: string | null
  middleNames: string[]
} {
  const { separator = ' ', lastNamePosition = 'last', normalize = true } = options

  // Normalize whitespace if requested
  const normalizedName = normalize ? fullName.trim().replace(/\s+/g, separator) : fullName

  if (!normalizedName) {
    return { firstName: '', lastName: null, middleNames: [] }
  }

  const parts = normalizedName.split(separator).filter((part) => part.length > 0)

  if (parts.length === 0) {
    return { firstName: '', lastName: null, middleNames: [] }
  }

  const [firstPart] = parts

  if (parts.length === 1) {
    return { firstName: firstPart ?? '', lastName: null, middleNames: [] }
  }

  if (lastNamePosition === 'none') {
    return { firstName: normalizedName, lastName: null, middleNames: [] }
  }

  if (lastNamePosition === 'first') {
    // First part is firstName, all remaining parts become lastName
    const firstName = firstPart ?? ''
    const lastName = parts.slice(1).join(separator)

    return {
      firstName,
      lastName,
      middleNames: [],
    }
  }

  // lastNamePosition === 'last'
  const firstName = firstPart ?? ''
  const lastName = parts[parts.length - 1] ?? ''
  const middleNames = parts.slice(1, -1)

  return {
    firstName,
    lastName,
    middleNames,
  }
}
